import FullScreenLoading from "@/components/FullScreenLoading";
import { AUTH_REDIRECT_URI_STORAGE_KEY } from "@/constants";
import { useLocaleSync } from "@/hooks/useLocaleSync";
import { useTokenRefresh } from "@/hooks/useTokenRefresh";
import {
  userManager,
  isIdleLogoutCallback,
} from "@/provider/AuthProvider/userManager";
import { prepareAuthSignOut } from "@/provider/AuthProvider/signOut";
import {
  hasOidcCallbackParams,
  isAuthCallbackPath,
} from "@/utils/silentTokenRefresh";
import { useState } from "react";
import type { AuthProviderBaseProps } from "react-oidc-context";
import { AuthProvider as OidcAuthProvider, useAuth } from "react-oidc-context";
import { useNavigate } from "react-router";
import IdleLogout from "./IdleLogout";

type NextAuthProviderProps = {
  children?: React.ReactNode;
};

function NextAuthProvider(props: NextAuthProviderProps) {
  const { children } = props;

  const { isLoading } = useAuth();

  useLocaleSync();
  useTokenRefresh();

  if (isLoading && !isAuthCallbackPath()) {
    return <FullScreenLoading />;
  }

  return children;
}

type AuthProviderProps = {
  children?: React.ReactNode;
};

function AuthProvider(props: AuthProviderProps) {
  const { children } = props;

  const navigate = useNavigate();

  const [modalOpen, setModalOpen] = useState(false);

  const handleSigninCallback: AuthProviderBaseProps["onSigninCallback"] =
    () => {
      const redirectPathname =
        sessionStorage.getItem(AUTH_REDIRECT_URI_STORAGE_KEY) ??
        window.location.pathname;
      navigate(redirectPathname);
    };

  const handleSignoutCallback: AuthProviderBaseProps["onSignoutCallback"] = (
    resp
  ) => {
    // Safety net when idle logout callback runs after redirect.
    prepareAuthSignOut();

    const userState = resp?.userState;
    if (
      typeof userState === "object" &&
      userState !== null &&
      "reason" in userState &&
      userState.reason === "idle"
    ) {
      navigate(
        sessionStorage.getItem(AUTH_REDIRECT_URI_STORAGE_KEY) ??
          window.location.pathname
      );
      setModalOpen(true);
    }
  };

  return (
    <OidcAuthProvider
      userManager={userManager}
      // Let AuthCallback run Serial PKCE exchange instead of the library default.
      skipSigninCallback={isAuthCallbackPath() && hasOidcCallbackParams()}
      onSigninCallback={handleSigninCallback}
      onSignoutCallback={handleSignoutCallback}
      matchSignoutCallback={() => isIdleLogoutCallback()}
    >
      <NextAuthProvider>
        {!modalOpen && children}
        {modalOpen && <IdleLogout />}
      </NextAuthProvider>
    </OidcAuthProvider>
  );
}

export default AuthProvider;
